import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { BASE_API_URL } from '../helpers/Config';
import axios from 'axios';
import moment from 'moment';
import { TriggerBalanceUpdate } from '../helpers/TriggerBalanceUpdate';

const CommissionClaim = ({ theme = 'dark' }) => {
    const [unclaimedCoins, setUnclaimedCoins] = useState(0);
    const [claimHistory, setClaimHistory] = useState([]);
    const [refreshing, setRefreshing] = useState(false);

    const navigate = useNavigate();
    const isDark = theme === 'dark';

    const styles = getStyles(isDark);

    const handleClaim = async () => {
        try {
            const formData = {
                token: localStorage.getItem('a5srf4wer7w1cd'),
            };

            const response = await axios.post(`${BASE_API_URL}/team/claim-commission`, formData);
            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                    return;
                } else {
                    toast.error(data?.error);
                    return;
                }
            }

            if (data?.msg) {
                toast.success(data?.msg);
                setUnclaimedCoins(0);
                FetchUnclaimed();
                FetchClaimHistory();
            }
            if (data?.balance) {
                TriggerBalanceUpdate({
                    balance: data?.balance,
                    usdt: data?.usdt,
                });
            }
        } catch (error) {
            toast.error('Failed to claim commission');
        }
    };

    const FetchClaimHistory = async () => {
        try {
            const formData = {
                token: localStorage.getItem('a5srf4wer7w1cd'),
            };

            const response = await axios.post(`${BASE_API_URL}/team/commission-claim-report`, formData);
            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                    return;
                } else {
                    toast.error(data?.error);
                    return;
                }
            }

            if (data?.data) {
                setClaimHistory(data?.data);
            }
        } catch (error) {
            toast.error('Failed to fetch claim history');
        }
    };

    const FetchUnclaimed = async () => {
        try {
            const formData = {
                token: localStorage.getItem('a5srf4wer7w1cd'),
            };

            const response = await axios.post(`${BASE_API_URL}/team/unclaimed-commission`, formData);
            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                    return;
                } else {
                    toast.error(data?.error);
                    return;
                }
            }

            if (typeof data?.unclaimed === 'number') {
                setUnclaimedCoins(data?.unclaimed);
            } else {
                setUnclaimedCoins(0);
            }
        } catch (error) {
            toast.error('Failed to fetch claim history');
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await Promise.all([FetchUnclaimed(), FetchClaimHistory()]);
            toast.success('Data refreshed successfully!');
        } catch (error) {
            toast.error('Failed to refresh data');
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        FetchUnclaimed();
        FetchClaimHistory();
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.5,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.5 }
        }
    };

    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            style={styles.container}
        >
            {/* Header */}
            <motion.div variants={itemVariants} className="container-fluid p-3">
                <div className="row align-items-center">
                    <div className="col">
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                                <span style={styles.icon} className="me-2">
                                    <i className="bi bi-wallet2"></i>
                                </span>
                                <h4 className="mb-0" style={styles.title}>Claim</h4>
                            </div>

                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => {
                                    navigate('../commission-record')
                                }}
                                disabled={refreshing}
                                style={styles.refreshButton}
                                className="btn btn-outline-primary btn-sm"
                            >
                                <motion.i
                                    className="bi bi-gift"
                                    animate={{ rotate: 0 }}
                                    transition={{
                                        rotate: {
                                            duration: 0.8,
                                            repeat: 0,
                                            ease: "linear"
                                        }
                                    }}
                                />
                                <span className="ms-1">
                                    Record
                                </span>
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleRefresh}
                                disabled={refreshing}
                                style={styles.refreshButton}
                                className="btn btn-outline-primary btn-sm"
                            >
                                <motion.i
                                    className="bi bi-arrow-clockwise"
                                    animate={{ rotate: refreshing ? 360 : 0 }}
                                    transition={{
                                        rotate: {
                                            duration: 0.8,
                                            repeat: refreshing ? Infinity : 0,
                                            ease: "linear"
                                        }
                                    }}
                                />
                                <span className="ms-1">
                                    {refreshing ? 'Refreshing...' : 'Refresh'}
                                </span>
                            </motion.button>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Unclaimed Coins Section */}
            <motion.div variants={itemVariants} className="container-fluid p-3">
                <div className="card shadow-sm" style={styles.card}>
                    <div className="card-body text-center">
                        <h6
                            className="card-subtitle mb-2 text-muted"
                            style={styles.sectionSubtitle}
                        >
                            Unclaimed Coins
                        </h6>
                        <h2 style={styles.coinAmount}>{unclaimedCoins.toFixed(2)}</h2>
                        <p className="text-muted" style={styles.minText}>
                            Minimum 10 coins required to claim
                        </p>

                        <motion.button
                            whileTap={{ scale: 0.95 }}
                            className={`btn w-100 ${unclaimedCoins >= 10 ? 'btn-primary' : 'btn-secondary'}`}
                            style={styles.claimButton}
                            onClick={handleClaim}
                            disabled={unclaimedCoins < 10}
                        >
                            {unclaimedCoins >= 10 ? 'Claim Now' : 'Insufficient Coins'}
                        </motion.button>
                    </div>
                </div>
            </motion.div>

            {/* Recent Claim History */}
            <motion.div variants={itemVariants} className="container-fluid p-3">
                <div className="card shadow-sm" style={styles.card}>
                    <div className="card-body">
                        <h6 className="card-title" style={styles.sectionTitle}>
                            <i className="bi bi-clock-history me-2"></i>
                            Recent Claim History
                        </h6>

                        {claimHistory.length === 0 ? (
                            <div className="text-center py-3">
                                <p className="text-muted" style={styles.emptyText}>No claim history yet</p>
                            </div>
                        ) : (
                            <div style={styles.historyList}>
                                {claimHistory.map((claim) => (
                                    <motion.div
                                        key={claim.claim_id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        className="d-flex justify-content-between align-items-center py-2 border-bottom"
                                        style={styles.historyItem}
                                    >
                                        <div>
                                            <strong style={styles.coinText}>{claim.amount}</strong>
                                            <br />
                                            <small className="text-muted" style={styles.dateText}>
                                                {moment(claim.create_date, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YY hh:mm A")}
                                            </small>
                                        </div>
                                        <div className="badge bg-success" style={styles.statusBadge}>
                                            Claimed
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>

            {/* Bootstrap Icons */}
            <link
                rel="stylesheet"
                href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css"
            />
        </motion.div>
    );
};

const getStyles = (isDark) => ({
    container: {
        minHeight: '100vh',
        backgroundColor: isDark ? '#020617' : '#f8f9fa',
        paddingBottom: '20px',
        color: isDark ? '#e5e7eb' : '#212529',
    },
    title: {
        fontWeight: 'bold',
        color: isDark ? '#e5e7eb' : '#2c3e50',
        fontSize: '20px'
    },
    icon: {
        fontSize: '22px',
        color: isDark ? '#fbbf24' : '#e67e22'
    },
    card: {
        borderRadius: '12px',
        border: isDark ? '1px solid #1f2937' : 'none',
        backgroundColor: isDark ? '#020617' : '#ffffff',
        boxShadow: isDark
            ? '0 10px 25px rgba(0,0,0,0.5)'
            : '0 .125rem .25rem rgba(0,0,0,.075)'
    },
    coinAmount: {
        fontWeight: 'bold',
        color: isDark ? '#f59e0b' : '#e67e22',
        margin: '8px 0',
        fontSize: '32px'
    },
    sectionSubtitle: {
        fontSize: '14px',
        fontWeight: '600',
        color: isDark ? '#9ca3af' : '#6c757d'
    },
    sectionTitle: {
        fontWeight: 'bold',
        color: isDark ? '#e5e7eb' : '#2c3e50',
        marginBottom: '15px',
        fontSize: '16px'
    },
    minText: {
        fontSize: '12px',
        marginBottom: '15px',
        color: isDark ? '#9ca3af' : '#6c757d'
    },
    emptyText: {
        fontSize: '14px',
        color: isDark ? '#9ca3af' : '#6c757d'
    },
    claimButton: {
        borderRadius: '20px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontSize: '14px',
        padding: '8px 16px',
        border: 'none'
    },
    historyList: {
        maxHeight: '300px',
        overflowY: 'auto'
    },
    historyItem: {
        padding: '8px 0',
        borderBottom: `1px solid ${isDark ? '#1f2937' : '#e5e7eb'}`
    },
    coinText: {
        color: isDark ? '#f59e0b' : '#e67e22',
        fontSize: '14px'
    },
    dateText: {
        fontSize: '11px',
        color: isDark ? '#9ca3af' : '#6c757d'
    },
    statusBadge: {
        borderRadius: '10px',
        padding: '4px 8px',
        fontSize: '11px',
        border: isDark ? '1px solid #22c55e' : 'none',
        backgroundColor: isDark ? 'rgba(34,197,94,0.15)' : undefined,
        color: isDark ? '#bbf7d0' : undefined,
    },
    refreshButton: {
        borderRadius: '20px',
        fontWeight: '600',
        fontSize: '12px',
        padding: '6px 12px',
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${isDark ? '#60a5fa' : '#007bff'}`,
        backgroundColor: isDark ? 'transparent' : '#ffffff',
        color: isDark ? '#e5e7eb' : '#0d6efd'
    }
});

export default CommissionClaim;
